import React, { useState } from 'react';
import { isStrongPassword } from '../../utils/password';
import { DESTINATION_OPTIONS } from '../../utils/destinations';

const emptyForm = { name: '', respondent: '', email: '', exportType: 'Otros', destination: '', password: '', confirmation: '' };
const inputClass = 'mt-1 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#004AAD]/20 focus:border-[#004AAD]';

export default function CompanyRegistrationForm({ onRegister, onBack }) {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const change = event => setForm(previous => ({ ...previous, [event.target.name]: event.target.value }));
  const passwordsMatch = form.password === form.confirmation;
  const canSubmit = form.name.trim() && form.respondent.trim() && form.email.trim() && isStrongPassword(form.password) && passwordsMatch;

  const submit = async event => {
    event.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    setError('');
    try {
      const { confirmation, ...payload } = form;
      await onRegister({ ...payload, name: form.name.trim(), respondent: form.respondent.trim(), email: form.email.trim().toLowerCase() });
      setForm(emptyForm);
      setSubmitted(true);
    } catch (registrationError) {
      setError(registrationError.code === 'functions/resource-exhausted'
        ? 'Se recibieron demasiadas solicitudes. Intenta más tarde.'
        : registrationError.message || 'No se pudo registrar la solicitud. Intenta nuevamente.');
    } finally {
      setBusy(false);
    }
  };

  if (submitted) return (
    <div className="flex-1 space-y-4 pb-6">
      <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 p-4">
        <h2 className="text-lg font-bold text-slate-900">Registro recibido</h2>
        <p className="mt-2 text-sm leading-relaxed text-slate-700">Tu cuenta está pendiente de aprobación por el equipo administrador.</p>
        <p className="mt-2 text-sm leading-relaxed text-slate-700">Una vez aprobada, podrás ingresar con el correo y la contraseña que elegiste. No necesitas confirmar ningún correo.</p>
      </div>
      <button type="button" onClick={onBack} className="w-full rounded-xl bg-[#004AAD] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#003B8A]">Volver al ingreso</button>
    </div>
  );

  return (
    <div className="flex-1 pb-6">
      <button type="button" onClick={onBack} disabled={busy} className="mb-4 text-xs font-semibold text-slate-500 hover:text-[#004AAD] disabled:opacity-50">← Volver al ingreso</button>
      <h2 className="text-xl font-bold text-slate-900">Crea tu cuenta</h2>
      <p className="mb-4 mt-1 text-xs leading-relaxed text-slate-500">Elige tu contraseña. El equipo administrador revisará tu registro y habilitará el acceso, sin verificación por correo.</p>
      <form onSubmit={submit} className="space-y-3">
        {error && <p role="alert" className="rounded-lg border border-red-100 bg-red-50 p-3 text-xs text-red-700">{error}</p>}
        <fieldset disabled={busy} className="grid min-w-0 grid-cols-1 gap-3 disabled:opacity-60 lg:grid-cols-2">
          <div>
            <label htmlFor="register-company" className="text-xs font-semibold text-slate-600">Empresa</label>
            <input id="register-company" name="name" value={form.name} onChange={change} autoComplete="organization" required maxLength={160} className={inputClass} />
          </div>
          <div>
            <label htmlFor="register-respondent" className="text-xs font-semibold text-slate-600">Nombre del responsable</label>
            <input id="register-respondent" name="respondent" value={form.respondent} onChange={change} autoComplete="name" required maxLength={160} className={inputClass} />
          </div>
          <div className="lg:col-span-2">
            <label htmlFor="register-email" className="text-xs font-semibold text-slate-600">Correo de acceso</label>
            <input id="register-email" type="email" name="email" value={form.email} onChange={change} autoComplete="username" autoCapitalize="none" required maxLength={254} aria-describedby="register-email-help" className={inputClass} />
            <p id="register-email-help" className="mt-1 text-xs text-slate-500">Lo usarás como usuario para iniciar sesión.</p>
          </div>
          <div>
            <label htmlFor="register-sector" className="text-xs font-semibold text-slate-600">Sector exportador</label>
            <select id="register-sector" name="exportType" value={form.exportType} onChange={change} required className={inputClass}>
              {['Banano', 'Camarón', 'Plátano', 'Mango', 'Brócoli', 'Atún', 'Flores', 'Cacao', 'Pescado', 'Palmito en conserva', 'Otros'].map(sector => <option key={sector}>{sector}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="register-destination" className="text-xs font-semibold text-slate-600">Destino principal (opcional)</label>
            <select id="register-destination" name="destination" value={form.destination} onChange={change} className={inputClass}>
              <option value="">Selecciona un destino</option>
              {DESTINATION_OPTIONS.map(destination => <option key={destination}>{destination}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="register-password" className="text-xs font-semibold text-slate-600">Contraseña</label>
            <input id="register-password" type="password" name="password" value={form.password} onChange={change} autoComplete="new-password" required minLength={10} maxLength={128} aria-describedby="register-password-help" className={inputClass} />
            <p id="register-password-help" className="mt-1 text-xs text-slate-500">Mínimo 10 caracteres, con mayúscula, minúscula y número.</p>
          </div>
          <div>
            <label htmlFor="register-confirmation" className="text-xs font-semibold text-slate-600">Confirmar contraseña</label>
            <input id="register-confirmation" type="password" name="confirmation" value={form.confirmation} onChange={change} autoComplete="new-password" required maxLength={128} aria-invalid={Boolean(form.confirmation && !passwordsMatch)} className={inputClass} />
            {form.confirmation && !passwordsMatch && <p className="mt-1 text-xs text-red-600">Las contraseñas no coinciden.</p>}
          </div>
          <button type="submit" disabled={!canSubmit || busy} className="w-full rounded-xl bg-[#004AAD] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#003B8A] disabled:cursor-not-allowed disabled:opacity-50 lg:col-span-2">{busy ? 'Registrando...' : 'Solicitar acceso'}</button>
        </fieldset>
      </form>
    </div>
  );
}
